/**
 * Building services (Phase 2). Each service is a building-wide system with a
 * PLANT tile you tamper with (via the channel verb) for a stealth effect. Every
 * service the building type REQUIRES gets a plant, seated in its own machine
 * room — the room types are forced into existence during floor generation
 * (`rooms.ts` PLANT_ROOMS, distributed basement-first by `building.ts`), so a
 * required service always has something to sabotage.
 *
 * Effects, in the order they land:
 *   - power    → cut it and the whole building goes dark (mass breaker blackout,
 *                which the existing blackout-witness + fusebox-restore handle).
 *   - comms    → cut it and guards can't call backup (broadcast suppressed).
 *   - water / hvac / boiler / fire / elevator → carry state now; their lighter
 *     effects (sound mask, alarm, forced stairs) land with the outlets, and the
 *     environmental physics (flood, smoke, explosion) stay Phase 8.
 *
 * Pure + deterministic.
 */

import { hashSeed, mulberry32 } from '../rng';
import { setTile, tileAt, type MapData } from '../map';
import { cellFloor } from './bsp';
import { riserTiles } from './shell';
import { TileType } from '../tiles';
import type { Building } from './building';
import { findSolidSpot } from './props';

export type ServiceKind = 'power' | 'comms' | 'water' | 'hvac' | 'boiler' | 'fire' | 'elevator';

/** The vital services a Corporate Office declares, each with the room type that
 *  houses its plant. Other building titles will declare their own subset. */
export const REQUIRED_SERVICES: ReadonlyArray<{ kind: ServiceKind; room: string }> = [
  { kind: 'power', room: 'utility' },   // electrical / switchgear
  { kind: 'comms', room: 'server' },    // MDF / patch panels
  { kind: 'water', room: 'pump' },      // pump room / water main
  { kind: 'boiler', room: 'boiler' },   // water heating
  { kind: 'hvac', room: 'hvac' },       // air handling
  { kind: 'fire', room: 'fire' },       // alarm + sprinkler control
  { kind: 'elevator', room: 'elevator' }, // hoist controller
];

export interface Service {
  kind: ServiceKind;
  /** Stack index of the floor the plant is on. */
  floorIdx: number;
  /** The interactable plant tile. */
  plant: { x: number; y: number };
  /** The service's vertical shaft: one tile inside the building core, at the
   *  SAME (x,y) on every floor (see `shell.riserTiles`). */
  riser: { x: number; y: number };
  /** Where the service surfaces out in the building — a tap on the riser on
   *  every floor, plus fixtures in the rooms that use it. */
  outlets: ServiceOutlet[];
  /** 'normal' = running as the building intends; 'tampered' = the player has
   *  channelled it. What that MEANS is per-kind (see `tamperService`): power
   *  tampered = dead, water tampered = running flat out. */
  state: 'normal' | 'tampered';
}

/** A place a service can be reached from without going to its plant room. */
export interface ServiceOutlet {
  kind: ServiceKind;
  floorIdx: number;
  x: number;
  y: number;
  /** 'riser' = the shaft tap in the core (one per floor, aligned); 'fixture' =
   *  the thing the service exists FOR, out in a room that uses it. */
  spot: 'riser' | 'fixture';
}

/** Rooms whose fixtures tap each service. A service with no listed room only
 *  surfaces on its riser. */
const FIXTURE_ROOMS: Record<ServiceKind, readonly string[]> = {
  power: ['security', 'server'],          // sub-panels where the load is
  comms: ['security', 'private-office'],  // network jacks
  water: ['restroom', 'break'],           // faucets + sinks
  boiler: ['restroom', 'break'],          // hot-water taps
  hvac: ['open-office', 'meeting', 'server'], // vent grilles
  fire: ['storage', 'records', 'server'], // sprinkler heads + alarm pulls
  elevator: ['lobby'],                    // call panel
};

const PLANT_GLYPH: Record<ServiceKind, string> = {
  power: '▨',    // main switchboard
  comms: '▧',    // patch panel / MDF
  water: '≈',    // pumps + main valve
  boiler: 'Θ',   // boiler drum
  hvac: 'ロ',     // air-handling unit
  fire: '†',     // sprinkler / alarm panel
  elevator: '╫', // hoist controller (NOT ≣ — that's the stairs glyph)
};

/** Seat a plant tile for `kind` in the first floor (in `order`) that has a room
 *  of `roomKind`, returning the Service or null if none could be placed. */
function placePlant(
  building: Building,
  kind: ServiceKind,
  roomKind: string,
  order: number[],
  rng: () => number,
): Service | null {
  for (const fi of order) {
    const F = building.floors[fi]!;
    const ri = F.map.rooms.findIndex((r) => r.kind === roomKind);
    if (ri < 0) continue;
    const spot = findSolidSpot(F.map, F.cells[ri]!, rng);
    if (!spot) continue;
    setTile(F.map, spot.x, spot.y, TileType.PropTall);
    F.map.glyphs[spot.y * F.map.width + spot.x] = PLANT_GLYPH[kind];
    return { kind, floorIdx: fi, plant: spot, riser: { x: 0, y: 0 }, outlets: [], state: 'normal' };
  }
  return null;
}

/**
 * Build the vital services for `building`: one plant per required service, in
 * its own machine room. Plant rooms are seated basement-first by generation, so
 * the search runs bottom-up — except COMMS, whose MDF sits with the server room
 * (searched top-down, where a corporate server floor usually is). A service
 * whose machine room didn't fit on any floor falls back to a Utility room, so a
 * cramped building still exposes every system somewhere.
 */
export function buildServices(building: Building): Service[] {
  const rng = mulberry32(hashSeed(building.establishment.seed, 0x5e2));
  const services: Service[] = [];
  const lowFirst = building.floors.map((_f, i) => i); // 0 = deepest basement
  const highFirst = lowFirst.slice().reverse();

  const risers = riserTiles(building.shell);
  REQUIRED_SERVICES.forEach((req, i) => {
    const order = req.kind === 'comms' ? highFirst : lowFirst;
    const svc = placePlant(building, req.kind, req.room, order, rng)
      ?? placePlant(building, req.kind, 'utility', order, rng);
    if (!svc) return;
    // One riser tile per service, spread around the core shaft. The core is
    // identical on every floor, so this position is aligned by construction.
    svc.riser = risers.length ? risers[(i * 2) % risers.length]! : svc.plant;
    svc.outlets = placeOutlets(building, svc);
    services.push(svc);
  });
  return services;
}

/**
 * Surface a service in the building: a tap on its riser on EVERY floor (so the
 * system is reachable wherever you are), plus a fixture in each floor's rooms
 * that actually use it — a faucet in the restroom, a grille in the open office.
 * Outlets are passable decor, so they never affect connectivity.
 */
function placeOutlets(building: Building, svc: Service): ServiceOutlet[] {
  const out: ServiceOutlet[] = [];
  const wantRooms = FIXTURE_ROOMS[svc.kind];
  for (const F of building.floors) {
    /** Claim a free floor tile as an outlet. Returns whether it took. */
    const put = (x: number, y: number, spot: 'riser' | 'fixture'): boolean => {
      if (tileAt(F.map, x, y) !== TileType.Floor) return false;
      setTile(F.map, x, y, TileType.PropDecor);
      F.map.glyphs[y * F.map.width + x] = PLANT_GLYPH[svc.kind];
      out.push({ kind: svc.kind, floorIdx: F.index, x, y, spot });
      return true;
    };
    put(svc.riser.x, svc.riser.y, 'riser');
    // One fixture per floor, in the first room of a type that uses the service,
    // against a wall (the interior ring) where a fixture would really sit.
    const ri = F.map.rooms.findIndex((r) => r.kind !== undefined && wantRooms.includes(r.kind));
    if (ri < 0) continue;
    const f = cellFloor(F.cells[ri]!);
    let placed = false;
    for (let x = f.x0; x <= f.x1 && !placed; x++) placed = put(x, f.y0, 'fixture');
  }
  return out;
}

/** Is (px,py) orthogonally adjacent to (or on) `s.plant`? */
export function adjacentToPlant(s: Service, px: number, py: number): boolean {
  return Math.abs(px - s.plant.x) + Math.abs(py - s.plant.y) <= 1;
}

/** The service whose plant the player stands next to on the active floor, or null. */
export function serviceAtHand(
  services: Service[],
  floorIdx: number,
  px: number,
  py: number,
): Service | null {
  return services.find((s) => s.floorIdx === floorIdx && s.state === 'normal' && adjacentToPlant(s, px, py)) ?? null;
}

/** The service outlet the player stands next to on the active floor, or null.
 *  Outlets are how a service is reached WITHOUT trekking to its plant room. */
export function outletAtHand(
  services: Service[],
  floorIdx: number,
  px: number,
  py: number,
): { service: Service; outlet: ServiceOutlet } | null {
  for (const s of services) {
    for (const o of s.outlets) {
      if (o.floorIdx !== floorIdx) continue;
      if (Math.abs(px - o.x) + Math.abs(py - o.y) <= 1) return { service: s, outlet: o };
    }
  }
  return null;
}

/** What a tamper did — the caller turns it into a log line + UI reaction. */
export interface TamperResult {
  kind: ServiceKind;
  message: string;
  /** The light field changed on these floor indices (caller may need to redraw). */
  relitFloors: number[];
}

/**
 * Channel a service into its tampered state and apply the part of the effect
 * that lives in the world model. Effects reuse existing systems wherever one
 * exists; the ambient ones (running water, the fire alarm) are emitted per tick
 * by `tickServices` rather than fired once here.
 *
 * Returns null if the service was already tampered.
 */
export function tamperService(building: Building, svc: Service): TamperResult | null {
  if (svc.state === 'tampered') return null;
  svc.state = 'tampered';
  const relitFloors: number[] = [];
  let message: string;
  switch (svc.kind) {
    case 'power':
      // Cut the mains: a building-wide breaker blackout. The existing
      // blackout-witness makes guards react; the fusebox restores rooms singly.
      for (const f of building.floors) {
        for (const r of f.map.rooms) r.lightsOn = false;
        relitFloors.push(f.index);
      }
      message = 'Main power cut — the building goes dark.';
      break;
    case 'comms':
      building.commsDown = true;
      message = "Comms sabotaged — guards can't call for backup.";
      break;
    case 'water':
      // Open the main: every faucet and riser tap in the building runs. The
      // noise is emitted at the OUTLETS each tick (tickServices) — cover to move
      // under, and a lure wherever a tap is.
      message = 'Water main opened — pipes hammer and every tap runs.';
      break;
    case 'fire':
      message = 'Fire alarm tripped — bells ring through the building.';
      break;
    case 'boiler':
      message = 'Boiler shut down — the building starts to cool.';
      break;
    case 'hvac':
      message = 'Air handling stopped — the vents fall silent.';
      break;
    case 'elevator':
      message = 'Hoist controller killed — the elevators are dead.';
      break;
  }
  return { kind: svc.kind, message, relitFloors };
}

/** Per-kind ambient noise a TAMPERED service pushes out of each of its outlets,
 *  in sound-flood loudness. 0 = silent (state-only effects). */
const AMBIENT_LOUDNESS: Record<ServiceKind, number> = {
  power: 0,
  comms: 0,
  water: 6,    // running water — a moving player's footsteps vanish under it
  fire: 14,    // alarm bells: loud enough to pull a floor's guards
  boiler: 0,
  hvac: 0,
  elevator: 0,
};

/** Seconds between ambient emissions, so a running tap doesn't spam the queue. */
const AMBIENT_PERIOD_SEC = 0.5;

/**
 * Run the sustained side of tampered services on the ACTIVE floor: a running
 * water main and a tripped fire alarm keep making noise out of every outlet
 * until they're dealt with. Call once per tick with the elapsed seconds; `emit`
 * is the caller's sound emitter (x, y, loudness).
 */
export function tickServices(
  services: readonly Service[],
  activeFloorIdx: number,
  dt: number,
  emit: (x: number, y: number, loudness: number) => void,
  clock: { t: number },
): void {
  const noisy = services.filter((s) => s.state === 'tampered' && AMBIENT_LOUDNESS[s.kind] > 0);
  if (noisy.length === 0) { clock.t = 0; return; }
  clock.t += dt;
  if (clock.t < AMBIENT_PERIOD_SEC) return;
  clock.t = 0;
  for (const s of noisy) {
    for (const o of s.outlets) {
      if (o.floorIdx === activeFloorIdx) emit(o.x, o.y, AMBIENT_LOUDNESS[s.kind]);
    }
  }
}

/** True if this map has any powered-on room (i.e. not already blacked out). */
export function anyRoomPowered(map: MapData): boolean {
  return map.rooms.some((r) => r.lightsOn);
}
